import { createContext, useContext, useEffect, useRef, useState } from "react";

const FocusTimerContext = createContext();

const STORAGE_KEY = "learnlia_timer";

const MODES = {
  focus: 1500,   // 25 min
  short: 300,    // 5 min
  long: 900,     // 15 min
};

export function FocusTimerProvider({ children }) {

  const intervalRef = useRef(null);
  const alarmRef = useRef(null);

  const [timer, setTimer] = useState({
    mode: "focus",
    totalTime: MODES.focus,
    remaining: MODES.focus,
    isRunning: false,
    startedAt: null,
    isMinimized: true,
  });

  /* ================================
     LOAD TIMER FROM STORAGE
  ================================= */

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      setTimer(parsed);
    }
  }, []);

  /* ================================
     SAVE TIMER TO STORAGE
  ================================= */

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(timer));
  }, [timer]);

  /* ================================
     LOAD REMINDER SOUND
  ================================= */

  useEffect(() => {
    alarmRef.current = new Audio("/sounds/timer-done.mp3");
  }, []);

  /* ================================
     ASK NOTIFICATION PERMISSION
  ================================= */

  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission();
    }
  }, []);

  /* ================================
     ACCURATE TIMER LOGIC
  ================================= */

  useEffect(() => {

    if (!timer.isRunning) return;

    intervalRef.current = setInterval(() => {

      setTimer((prev) => {

        const elapsed = Math.floor(
          (Date.now() - prev.startedAt) / 1000
        );

        const newRemaining = prev.totalTime - elapsed;

        /* ===== TIMER FINISHED ===== */

        if (newRemaining <= 0) {

          clearInterval(intervalRef.current);

          /* PLAY REMINDER SOUND */

          if (alarmRef.current) {
            alarmRef.current.currentTime = 0;
            alarmRef.current.play().catch(() => {});
          }

          /* BROWSER NOTIFICATION */

          if (
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            new Notification("⏰ Timer Finished!", {
              body:
                prev.mode === "focus"
                  ? "Time for a break!"
                  : "Break finished. Time to focus!",
            });
          }

          return {
            ...prev,
            remaining: 0,
            isRunning: false,
            startedAt: null,
          };
        }

        return {
          ...prev,
          remaining: newRemaining,
        };

      });

    }, 1000);

    return () => clearInterval(intervalRef.current);

  }, [timer.isRunning]);

  /* ================================
     TAB TITLE SYNC
  ================================= */

  useEffect(() => {

    if (!timer.isRunning && timer.remaining === timer.totalTime) {
      document.title = "Learnlia";
      return;
    }

    const mins = Math.floor(timer.remaining / 60)
      .toString()
      .padStart(2, "0");

    const secs = (timer.remaining % 60)
      .toString()
      .padStart(2, "0");

    const label =
      timer.mode.charAt(0).toUpperCase() +
      timer.mode.slice(1);

    document.title = timer.isRunning
      ? `${mins}:${secs} • ${label}`
      : `⏸ ${mins}:${secs} • ${label}`;

  }, [timer]);

  /* ================================
     CONTROLS
  ================================= */

  const start = () => {
    setTimer((prev) => ({
      ...prev,
      isRunning: true,
      startedAt:
        Date.now() -
        (prev.totalTime - prev.remaining) * 1000,
    }));
  };

  const pause = () => {
    setTimer((prev) => ({
      ...prev,
      isRunning: false,
      startedAt: null,
    }));
  };

  const reset = () => {
    setTimer((prev) => ({
      ...prev,
      remaining: prev.totalTime,
      isRunning: false,
      startedAt: null,
    }));
  };

  const changeMode = (mode) => {
    setTimer((prev) => ({
      ...prev,
      mode,
      totalTime: MODES[mode],
      remaining: MODES[mode],
      isRunning: false,
      startedAt: null,
    }));
  };

  const toggleMinimize = () => {
    setTimer((prev) => ({
      ...prev,
      isMinimized: !prev.isMinimized,
    }));
  };

  return (
    <FocusTimerContext.Provider
      value={{
        timer,
        start,
        pause,
        reset,
        changeMode,
        toggleMinimize,
      }}
    >
      {children}
    </FocusTimerContext.Provider>
  );
}

export const useFocusTimer = () => useContext(FocusTimerContext);